import {
  MinusCircleOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CHALLAN_LIST,
  GET_SUPPLIER_ENDPOINT,
  INVOICE_BY_SUPPLIER,
  RAW_MATERIAL_INVENTORY_EDIT,
  RAW_MATERIAL_UPDATE,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import HandleNotification from "../../../common/Notification";
import StoreRequisitionNoSelect from "common/StoreRequisitionNoSelect";
import { commaSeparateNumber } from "../../../utils/functions";
import { onGetWarehouseList } from "../../../redux/actions";

const { Text } = Typography;

const INCOMING_MOVEMENT_TYPES = [
  "receive_from_supplier",
  "receive_from_sub_con_factory",
  "return_from_cutting",
  "return_from_sewing",
  "return_from_folding",
  "return_from_packing",
  "assign_from_free_balance",
  "return_from_cutting_final",
  "return_from_buying_office",
  "return_from_cutting_loose",
  "return_from_cutting_final_loose",
];

const OUTGOING_MOVEMENT_TYPES = [
  "issue_to_cutting",
  "issue_to_sewing",
  "issue_to_folding",
  "issue_to_packing",
  "mark_as_free",
  "issue_to_sample",
  "send_to_buying_office",
  "issue_to_lab",
  "issue_to_cutting_final",
  "stock_sale",
  "return_to_supplier",
  "return_to_sub_con_factory",
  "disposal",
  "issue_to_others",
  "issue_to_cutting_loose",
  "issue_to_cutting_final_loose",
];

const createEmptyPackage = () => ({
  id: null,
  package_id: null,
  details_id: null,
  package_detail_id: null,
  package_name: "",
  qty: null,
  loose_status: "no",
  is_delete: 0,
});

const resolvePackageDetailId = (packageItem) => {
  // Always use track_package_details primary id from API.
  // New rows must send null.
  const value =
    packageItem?.package_detail_id ?? packageItem?.details_id ?? null;

  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : null;
};

const resolvePackageId = (packageItem) => {
  // track_package.id from API
  const value = packageItem?.package_id ?? packageItem?.id ?? null;

  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : null;
};

const mapPackagesForInventory = (trackPackages = [], inventoryDetailId) => {
  if (!Array.isArray(trackPackages) || trackPackages.length === 0) {
    return [createEmptyPackage()];
  }

  const mapped = trackPackages
    .map((packageItem) => {
      const details = Array.isArray(packageItem?.track_package_details)
        ? packageItem.track_package_details
        : [];
      const detailItem =
        details.find(
          (item) =>
            Number(item?.inventory_detail_id) === Number(inventoryDetailId),
        ) || null;

      // Skip packages that belong to other inventory entries
      if (!detailItem) {
        return null;
      }

      return {
        id: packageItem?.id ?? null,
        package_id: packageItem?.id ?? null,
        details_id: detailItem?.id ?? null,
        package_detail_id: detailItem?.id ?? null,
        package_name: packageItem?.package_name || "",
        qty: Number(detailItem?.qty ?? packageItem?.qty ?? 0),
        loose_status: detailItem?.loose_status || "no",
        is_delete: 0,
      };
    })
    .filter(Boolean)
    .filter((packageItem) => Number(packageItem?.qty) !== 0);

  return mapped.length > 0 ? mapped : [createEmptyPackage()];
};

const normalizeTrackRows = (data) => {
  const raw = data?.track_item_histories ?? data?.track_item_history;
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const usedPackageDetailIds = new Set();

  return list.map((th) => {
    const ti = th.track_item || {};
    const bin = ti.bins_info || {};
    const rack = bin.rack_info || {};
    const wh = rack.warehouse_info || {};
    const allPackages = mapPackagesForInventory(
      ti.track_package,
      data?.id,
    ).filter((packageItem) => {
      // Avoid duplicating the same package detail across multiple history rows
      if (!packageItem?.package_detail_id) {
        return true;
      }
      if (usedPackageDetailIds.has(packageItem.package_detail_id)) {
        return false;
      }
      usedPackageDetailIds.add(packageItem.package_detail_id);
      return true;
    });

    const packages =
      allPackages.length > 0 ? allPackages : [createEmptyPackage()];

    return {
      history_id: th.id,
      track_id: th.track_id ?? ti.id,
      bin_id: ti.bin_id ?? bin?.id,
      warehouse_id: rack.warehouse_id ?? wh.id,
      rack_id: bin.rack_id ?? rack.id,
      warehouse_label: wh.name || "—",
      rack_label: rack.name || "—",
      bin_label: bin.name || "—",
      total_balance: Number(ti.total_quantity ?? 0),
      total_shipment: Number(ti.shipment_qty ?? 0),
      total_quantity: Number(th.total_quantity ?? 0),
      shipment_qty: Number(th.shipment_qty ?? 0),
      convertion_unit_id: th.convertion_unit_id ?? null,
      convertion_rate: th.convertion_rate ?? null,
      packages,
      is_delete: 0,
    };
  });
};

const FabricStoreEntryEditModal = ({ open, recordId, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const warehouseList = useSelector(
    ({ warehouse }) => warehouse.warehouseList,
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [supplierList, setSupplierList] = useState([]);
  const [challanList, setChallanList] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const tracksWatch = Form.useWatch("tracks", form);
  const entryDateWatch = Form.useWatch("entry_date", form);

  const newPackage = createEmptyPackage;

  const ensureSupplierInList = useCallback((data) => {
    const supplierId = Number(data?.supplier_id ?? data?.supplier_info?.id);
    const supplierName = data?.supplier_info?.name;
    if (!supplierId || !supplierName) return;

    setSupplierList((prev) => {
      const exists = prev.some((item) => Number(item?.id) === supplierId);
      if (exists) return prev;
      return [...prev, { id: supplierId, name: supplierName }];
    });
  }, []);

  const trackSums = useMemo(() => {
    const rows = Array.isArray(tracksWatch)
      ? tracksWatch.filter((row) => !row?.is_delete)
      : [];
    return rows.reduce(
      (acc, row) => {
        acc.totalBalance += Number(row?.total_quantity ?? 0);
        acc.totalShipment += Number(row?.shipment_qty ?? 0);
        return acc;
      },
      { totalBalance: 0, totalShipment: 0 },
    );
  }, [tracksWatch]);

  const movementType = detail?.movement_type || "";

  const actionLabel = useMemo(() => {
    if (INCOMING_MOVEMENT_TYPES.includes(movementType)) return "Receive";
    if (OUTGOING_MOVEMENT_TYPES.includes(movementType)) return "Issue";
    // fallback (in case new movement types are added)
    return movementType?.includes("receive") ? "Receive" : "Issue";
  }, [movementType]);

  const isReceiveFromSupplier = movementType === "receive_from_supplier";
  const isReceiveFromSubConFactory = movementType === "receive_from_sub_con_factory";
  const isReturnToSupplier = movementType === "return_to_supplier";
  const isReturnToSubConFactory = movementType === "return_to_sub_con_factory";
  const isIssueToLab = movementType === "issue_to_lab";
  const isSendToBuyingOffice = movementType === "send_to_buying_office";
  const isIssueToOthers = movementType === "issue_to_others";
  const isDisposal = movementType === "disposal";
  const isIssueType = Boolean(movementType?.includes?.("issue"));

  const incomingRequisitionRequiredTypes = [
    "return_from_cutting",
    "return_from_sewing",
    "return_from_folding",
    "return_from_finishing",
    "return_from_packing",
    "return_from_cutting_final",
    "return_from_cutting_loose",
    "return_from_cutting_final_loose",
  ];
  const outgoingRequisitionRequiredTypes = [
    "issue_to_cutting",
    "issue_to_sewing",
    "issue_to_folding",
    "issue_to_finishing",
    "issue_to_packing",
    "issue_to_sample",
    "issue_to_cutting_final",
    "issue_to_cutting_loose",
    "issue_to_cutting_final_loose",
  ];
  const showSupplier = isDisposal || isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier;
  const showInvoice = isDisposal || isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier;
  const showChallan = isDisposal || isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier;
  const showRequisition =
    isDisposal ||
    isIssueToLab ||
    isSendToBuyingOffice ||
    isIssueToOthers ||
    incomingRequisitionRequiredTypes.includes(movementType) ||
    outgoingRequisitionRequiredTypes.includes(movementType);
  const showReferenceNo = isIssueToLab || isSendToBuyingOffice || isIssueToOthers;
  const showGatePass = isSendToBuyingOffice || isReturnToSubConFactory;
  const requireRequisition =
    isIssueToLab ||
    isSendToBuyingOffice ||
    isIssueToOthers ||
    incomingRequisitionRequiredTypes.includes(movementType) ||
    outgoingRequisitionRequiredTypes.includes(movementType);
  const supplierFieldsDisabled = isReceiveFromSupplier && !entryDateWatch;
  const loadSuppliers = useCallback(async (name = "") => {
    const res = await getData(GET_SUPPLIER_ENDPOINT, false, { per_page: 100, name });
    if (res?.data?.code === 200) {
      setSupplierList(res?.data?.data?.data || []);
    }
  }, []);

  const loadChallans = useCallback(async (name = "") => {
    const res = await getData(CHALLAN_LIST, false, { per_page: 500, name });
    if (res?.data?.code === 200) {
      setChallanList(res?.data?.data?.data || []);
    }
  }, []);

  const loadInvoicesBySupplier = useCallback(async (supplierId) => {
    if (!supplierId) {
      setInvoiceList([]);
      return;
    }
    const res = await getData(`${INVOICE_BY_SUPPLIER}/${supplierId}`);
    if (res?.data?.code === 200) {
      setInvoiceList(res?.data?.data || []);
    }
  }, []);

  useEffect(() => {
    if (!open || !recordId) return;

    dispatch(onGetWarehouseList());
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setDetail(null);
      form.resetFields();

      try {
        const res = await getData(RAW_MATERIAL_INVENTORY_EDIT(recordId));
        if (cancelled) return;

        if (res?.data?.code !== 200) {
          HandleNotification("error", "bottomRight", res?.data?.message?.[0] || "Failed to load", null);
          onClose();
          return;
        }

        const data = res.data.data;
        setDetail(data);

        const tracks = normalizeTrackRows(data).map((track) => {
          const activePackages = (track.packages || []).filter(
            (packageItem) => !packageItem?.is_delete,
          );
          return {
            ...track,
            shipment_qty: activePackages.filter(
              (packageItem) => packageItem?.loose_status !== "yes",
            ).length,
            total_quantity: activePackages.reduce(
              (total, packageItem) => total + Number(packageItem?.qty || 0),
              0,
            ),
          };
        });

        const totalShipment = tracks.reduce(
          (total, track) =>
            track?.is_delete ? total : total + Number(track?.shipment_qty || 0),
          0,
        );
        const totalQuantity = tracks.reduce(
          (total, track) =>
            track?.is_delete
              ? total
              : total + Number(track?.total_quantity || 0),
          0,
        );

        form.setFieldsValue({
          entry_date: data.entry_date ? moment(data.entry_date, "YYYY-MM-DD") : undefined,
          supplier_id: data.supplier_id != null ? String(data.supplier_id) : undefined,
          invoice_id: data.invoice_id ?? undefined,
          challan_id: data.challan_id ?? undefined,
          store_requisition_id: data.store_requisition_id ?? undefined,
          requisition_no: data.requisition_no ?? undefined,
          reference_no: data.reference_no ?? undefined,
          gate_pass: data.gate_pass ?? undefined,
          received_total_quantity: totalQuantity,
          shipment_qty: totalShipment,
          tracks,
        });

        await Promise.all([loadSuppliers(), loadChallans()]);
        ensureSupplierInList(data);
        if (data.supplier_id) {
          await loadInvoicesBySupplier(data.supplier_id);
        } else {
          setInvoiceList([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, recordId, form, loadSuppliers, loadChallans, loadInvoicesBySupplier, onClose, ensureSupplierInList, dispatch]);

  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(tracksWatch) || tracksWatch.length === 0) return;

    const currentReceived = Number(form.getFieldValue("received_total_quantity") ?? 0);
    const currentShipment = Number(form.getFieldValue("shipment_qty") ?? 0);
    const nextReceived = Number(trackSums.totalBalance.toFixed(2));
    const nextShipment = Number(trackSums.totalShipment.toFixed(2));

    if (currentReceived !== nextReceived || currentShipment !== nextShipment) {
      form.setFieldsValue({
        received_total_quantity: nextReceived,
        shipment_qty: nextShipment,
      });
    }
  }, [open, tracksWatch, trackSums, form]);

  const onSupplierChange = (supplierId) => {
    form.setFieldsValue({ invoice_id: undefined });
    loadInvoicesBySupplier(supplierId);
  };

  const calculatePackageTotals = (_, changedValues) => {
    const values = changedValues || form.getFieldsValue();
    const tracks = (values?.tracks || []).map((track) => {
      if (track?.is_delete) return track;

      const packages = (track?.packages || []).filter(
        (packageItem) => !packageItem?.is_delete,
      );

      return {
        ...track,
        shipment_qty: packages.filter(
          (packageItem) => packageItem?.loose_status !== "yes",
        ).length,
        total_quantity: packages.reduce(
          (total, packageItem) => total + Number(packageItem?.qty || 0),
          0,
        ),
      };
    });

    form.setFieldsValue({ tracks });
  };

  const onWarehouseChange = (trackIndex) => {
    form.setFields([
      { name: ["tracks", trackIndex, "rack_id"], value: undefined },
      { name: ["tracks", trackIndex, "bin_id"], value: undefined },
    ]);
  };

  const onRackChange = (trackIndex) => {
    form.setFieldValue(["tracks", trackIndex, "bin_id"], undefined);
  };

  const removePackage = (trackIndex, packageIndex, remove) => {
    const packageItem = form.getFieldValue([
      "tracks",
      trackIndex,
      "packages",
      packageIndex,
    ]);

    if (
      packageItem?.id ||
      packageItem?.package_id ||
      packageItem?.details_id ||
      packageItem?.package_detail_id
    ) {
      form.setFieldValue(
        ["tracks", trackIndex, "packages", packageIndex, "is_delete"],
        1,
      );
    } else {
      remove(packageIndex);
    }
    calculatePackageTotals();
  };

  const removeLocation = (trackIndex, remove) => {
    const track = form.getFieldValue(["tracks", trackIndex]);

    if (track?.history_id || track?.track_id) {
      form.setFieldValue(["tracks", trackIndex], {
        ...track,
        is_delete: 1,
        packages: (track?.packages || []).map((packageItem) => ({
          ...packageItem,
          is_delete: 1,
        })),
      });
    } else {
      remove(trackIndex);
    }
    calculatePackageTotals();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!detail) return;

      setSaving(true);

      const formTracks = form.getFieldValue("tracks") || [];
      const locations =
        values.tracks?.map((row, trackIndex) => {
          const watchedPackages =
            formTracks?.[trackIndex]?.packages || row.packages || [];

          return {
            id: Number(row.history_id) || row.history_id,
            track_id: Number(row.track_id) || row.track_id,
            bin_id: Number(row.bin_id) || row.bin_id,
            shipment_qty: Number(row.shipment_qty ?? 0),
            total_quantity: Number(row.total_quantity ?? 0),
            is_delete: row.is_delete || 0,
            convertion_unit_id:
              Number(row.convertion_unit_id) ||
              detail.convertion_unit_id ||
              detail.convertion_unit?.id ||
              1,
            convertion_rate:
              row.convertion_rate != null && row.convertion_rate !== ""
                ? Number(row.convertion_rate)
                : Number(detail.convertion_rate) || 1,
            packages: watchedPackages.map((packageItem) => ({
              package_id: resolvePackageId(packageItem),
              package_detail_id: resolvePackageDetailId(packageItem),
              package_name: packageItem?.package_name,
              qty: Number(packageItem?.qty || 0),
              loose_status: packageItem?.loose_status || "no",
              is_delete: packageItem?.is_delete || 0,
            })),
          };
        }) ?? [];

      const bodyData = {
        movement_type: detail.movement_type,
        received_total_quantity: Number(values.received_total_quantity ?? 0),
        shipment_qty: Number(values.shipment_qty ?? 0),
        supplier_id: showSupplier ? values.supplier_id ?? null : null,
        invoice_id: showInvoice ? values.invoice_id ?? null : null,
        challan_id: showChallan ? values.challan_id ?? null : null,
        requisition_no: showRequisition
          ? values.store_requisition_id ?? values.requisition_no ?? null
          : null,
        store_requisition_id: showRequisition
          ? values.store_requisition_id ?? values.requisition_no ?? null
          : null,
        reference_no: showReferenceNo ? values.reference_no ?? null : null,
        gate_pass: showGatePass ? values.gate_pass ?? null : null,
        entry_date: values.entry_date ? moment(values.entry_date).format("YYYY-MM-DD") : detail.entry_date,
        locations,
      };

      const response = await postData(`${RAW_MATERIAL_UPDATE}/${detail.id}`, bodyData);

      if (response && response?.code === 200) {
        HandleNotification("success", "bottomRight", "Updated successfully", null);
        onSaved?.();
        onClose();
      } else {
        HandleNotification("error", "bottomRight", response?.message?.[0] || "Update failed", null);
      }
    } catch (e) {
      if (e?.errorFields) return;
      HandleNotification("error", "bottomRight", "Something went wrong", null);
    } finally {
      setSaving(false);
    }
  };

  const { Option } = Select;

  return (
    <Modal
      title="Edit fabric store entry"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={saving}
      width={1100}
      destroyOnClose
      maskClosable={false}
    >
      <Spin spinning={loading}>
        {detail && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              {detail.style_info?.style_no && <>Style: {detail.style_info.style_no} · </>}
              {detail.inventory_info?.item_info?.name && <>{detail.inventory_info.item_info.name}</>}
            </Text>
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          size="small"
          onValuesChange={calculatePackageTotals}
        >
          <Row gutter={12}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Date"
                name="entry_date"
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabledDate={(current) => current && current.endOf("day").isAfter(moment().endOf("day"))}
                />
              </Form.Item>
            </Col>
            {showSupplier && (
            <Col xs={24} sm={8}>
              <Form.Item label="Supplier" name="supplier_id" rules={[{ required: isReceiveFromSupplier || isReceiveFromSubConFactory || isReturnToSupplier, message: "Required" }]}>
                <Select
                  allowClear
                  showSearch
                  placeholder="Supplier"
                  optionFilterProp="children"
                  onSearch={(v) => loadSuppliers(v)}
                  filterOption={false}
                  onChange={onSupplierChange}
                  disabled={supplierFieldsDisabled}
                >
                  {supplierList.map((s) => (
                    <Option key={s.id} value={String(s.id)}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            )}
            {showInvoice && (
            <Col xs={24} sm={8}>
              <Form.Item label="Invoice" name="invoice_id">
                <Select allowClear showSearch placeholder="Invoice" optionFilterProp="children" disabled={supplierFieldsDisabled}>
                  {invoiceList.map((inv) => (
                    <Option key={inv.id} value={inv.id}>
                      {inv.invoice_number || inv.invoice_no || inv.number || inv.id}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            )}
            {showChallan && (
            <Col xs={24} sm={8}>
              <Form.Item label="Challan" name="challan_id">
                <Select
                  allowClear
                  showSearch
                  placeholder="Challan"
                  optionFilterProp="children"
                  onSearch={(v) => loadChallans(v)}
                  filterOption={false}
                  disabled={supplierFieldsDisabled}
                >
                  {challanList.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.challan_no || c.name || c.id}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            )}
            {showRequisition && (
            <Col xs={24} sm={8}>
              <Form.Item
                label="Requisition No"
                name="store_requisition_id"
                className=""
                rules={[{ required: requireRequisition, message: "Required" }]}
              >
                <StoreRequisitionNoSelect
                  movementType={movementType}
                  onRequisitionChange={(storeRequisitionId) => {
                    form.setFieldsValue({
                      store_requisition_id: storeRequisitionId || undefined,
                      requisition_no: storeRequisitionId || null,
                    });
                  }}
                />
              </Form.Item>
              <Form.Item name="requisition_no" hidden>
                <Input />
              </Form.Item>
            </Col>
            )}
            {showReferenceNo && (
            <Col xs={24} sm={8}>
              <Form.Item
                label="Reference No"
                name="reference_no"
                rules={[{ required: isIssueToLab || isIssueToOthers || isSendToBuyingOffice, message: "Required" }]}
              >
                <Input size="small" placeholder="Reference no." />
              </Form.Item>
            </Col>
            )}
            {showGatePass && (
            <Col xs={24} sm={8}>
              <Form.Item label="Gate Pass" name="gate_pass" rules={[{ required: true, message: "Required" }]}>
                <Input size="small" placeholder="Gate pass" />
              </Form.Item>
            </Col>
            )}
            <Col xs={24} sm={8}>
              <Form.Item
                label={`${actionLabel} total qty`}
                name="received_total_quantity"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled
                  min={0}
                  formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                  parser={(v) => (v ? String(v).replace(/,/g, "") : "")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label={`${actionLabel} Roll`}
                name="shipment_qty"
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  disabled
                  min={0}
                  formatter={(v) => (v === undefined || v === null ? "" : commaSeparateNumber(v))}
                  parser={(v) => (v ? String(v).replace(/,/g, "") : "")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Typography.Title level={5} style={{ marginTop: 8 }}>
            Warehouse Tracking
          </Typography.Title>

          <Form.List name="tracks">
            {(fields, { add, remove }) =>
              fields.length === 0 ? (
                <Text type="secondary">No warehouse tracking for this entry.</Text>
              ) : (
                <>
                  {fields.map((field, trackIndex) => {
                    const track = tracksWatch?.[trackIndex] || {};
                    if (track?.is_delete) return null;

                    const displayWarehouseIndex = (
                      tracksWatch || []
                    )
                      .slice(0, trackIndex + 1)
                      .filter((item) => !item?.is_delete).length;
                    const warehouse = warehouseList?.find(
                      (item) =>
                        Number(item?.id) === Number(track?.warehouse_id),
                    );
                    const racks = warehouse?.racks_info || [];
                    const rack = racks.find(
                      (item) => Number(item?.id) === Number(track?.rack_id),
                    );
                    const cells = rack?.bins_info || [];
                    const activePackageCount = (track?.packages || []).filter(
                      (packageItem) => !packageItem?.is_delete,
                    ).length;

                    return (
                      <Card
                        key={field.key}
                        size="small"
                        title={`Warehouse ${displayWarehouseIndex}`}
                        extra={
                          !isIssueType && (
                            <Button
                              danger
                              size="small"
                              icon={<MinusCircleOutlined />}
                              onClick={() =>
                                removeLocation(trackIndex, remove)
                              }
                            >
                              Remove Warehouse
                            </Button>
                          )
                        }
                        style={{
                          borderColor: "#d9e2e8",
                          borderRadius: 8,
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                          marginBottom: 16,
                        }}
                        headStyle={{
                          backgroundColor: "#f5f9fc",
                          borderRadius: "8px 8px 0 0",
                          minHeight: 40,
                        }}
                        bodyStyle={{ padding: 12 }}
                      >
                        <Row gutter={8} align="middle">
                          <Col span={0}>
                            <Form.Item name={[field.name, "history_id"]} hidden />
                            <Form.Item name={[field.name, "track_id"]} hidden />
                            <Form.Item name={[field.name, "is_delete"]} hidden />
                            <Form.Item
                              name={[field.name, "convertion_unit_id"]}
                              hidden
                            />
                            <Form.Item
                              name={[field.name, "convertion_rate"]}
                              hidden
                            />
                          </Col>
                          <Col xs={24} sm={4}>
                            <Form.Item
                              label="Warehouse"
                              name={[field.name, "warehouse_id"]}
                              rules={[{ required: true, message: "Required" }]}
                            >
                              <Select
                                placeholder="Warehouse"
                                disabled={isIssueType}
                                onChange={() =>
                                  onWarehouseChange(trackIndex)
                                }
                              >
                                {warehouseList?.map((item) => (
                                  <Option key={item.id} value={item.id}>
                                    {item.name}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={3}>
                            <Form.Item
                              label="Rack"
                              name={[field.name, "rack_id"]}
                              rules={[{ required: true, message: "Required" }]}
                            >
                              <Select
                                placeholder="Rack"
                                disabled={isIssueType}
                                onChange={() => onRackChange(trackIndex)}
                              >
                                {racks.map((item) => (
                                  <Option key={item.id} value={item.id}>
                                    {item.name}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={3}>
                            <Form.Item
                              label="Cell"
                              name={[field.name, "bin_id"]}
                              rules={[{ required: true, message: "Required" }]}
                            >
                              <Select placeholder="Cell" disabled={isIssueType}>
                                {cells.map((item) => (
                                  <Option key={item.id} value={item.id}>
                                    {item.name}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={3}>
                            <Form.Item
                              label="Balance"
                              name={[field.name, "total_balance"]}
                            >
                              <InputNumber
                                style={{ width: "100%" }}
                                disabled
                                formatter={(value) =>
                                  commaSeparateNumber(value)
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={3}>
                            <Form.Item
                              label="Balance Roll"
                              name={[field.name, "total_shipment"]}
                            >
                              <InputNumber
                                style={{ width: "100%" }}
                                disabled
                                formatter={(value) =>
                                  commaSeparateNumber(value)
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4}>
                            <Form.Item
                              label="Roll"
                              name={[field.name, "shipment_qty"]}
                            >
                              <InputNumber
                                style={{ width: "100%" }}
                                disabled
                                formatter={(value) =>
                                  commaSeparateNumber(value)
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={4}>
                            <Form.Item
                              label={`Qty in ${
                                detail?.inventory_info?.unit_info?.name || ""
                              }`}
                              name={[field.name, "total_quantity"]}
                            >
                              <InputNumber
                                style={{ width: "100%" }}
                                disabled
                                formatter={(value) =>
                                  commaSeparateNumber(value)
                                }
                              />
                            </Form.Item>
                          </Col>
                        </Row>

                        <div
                          style={{
                            border: "1px solid #d9d9d9",
                            borderRadius: 6,
                            overflow: "hidden",
                          }}
                        >
                          <Row
                            gutter={8}
                            style={{
                              backgroundColor: "#39769d",
                              padding: "7px 10px",
                            }}
                          >
                            <Col span={2}>
                              <Text strong style={{ color: "#fff" }}>
                                Count
                              </Text>
                            </Col>
                            <Col span={7}>
                              <Text strong style={{ color: "#fff" }}>
                                Loose Status
                              </Text>
                            </Col>
                            <Col span={7}>
                              <Text strong style={{ color: "#fff" }}>
                                Roll No
                              </Text>
                            </Col>
                            <Col span={6}>
                              <Text strong style={{ color: "#fff" }}>
                                Qty
                              </Text>
                            </Col>
                            <Col span={2} style={{ textAlign: "center" }}>
                              <Text strong style={{ color: "#fff" }}>
                                Actions
                              </Text>
                            </Col>
                          </Row>

                          <div style={{ padding: "8px 10px 2px" }}>
                            <Form.List name={[field.name, "packages"]}>
                              {(
                                packageFields,
                                {
                                  add: addPackage,
                                  remove: removePackageField,
                                },
                              ) => (
                                <>
                                  {packageFields.map(
                                    (packageField, packageIndex) => {
                                      const packageItem =
                                        track?.packages?.[packageIndex] || {};
                                      if (packageItem?.is_delete) return null;
                                      const displayIndex = (
                                        track?.packages || []
                                      )
                                        .slice(0, packageIndex + 1)
                                        .filter(
                                          (item) => !item?.is_delete,
                                        ).length;

                                      const setLooseStatus = (value) => {
                                        form.setFieldValue(
                                          [
                                            "tracks",
                                            trackIndex,
                                            "packages",
                                            packageIndex,
                                            "loose_status",
                                          ],
                                          value,
                                        );
                                        calculatePackageTotals();
                                      };

                                      return (
                                        <Row
                                          gutter={8}
                                          key={packageField.key}
                                          align="middle"
                                          style={{ minHeight: 40 }}
                                        >
                                          <Col span={2}>
                                            {displayIndex}
                                            <Form.Item
                                              name={[packageIndex, "id"]}
                                              hidden
                                            >
                                              <Input type="hidden" />
                                            </Form.Item>
                                            <Form.Item
                                              name={[packageIndex, "package_id"]}
                                              hidden
                                            >
                                              <Input type="hidden" />
                                            </Form.Item>
                                            <Form.Item
                                              name={[
                                                packageIndex,
                                                "details_id",
                                              ]}
                                              hidden
                                            >
                                              <Input type="hidden" />
                                            </Form.Item>
                                            <Form.Item
                                              name={[
                                                packageIndex,
                                                "package_detail_id",
                                              ]}
                                              hidden
                                            >
                                              <Input type="hidden" />
                                            </Form.Item>
                                            <Form.Item
                                              name={[
                                                packageIndex,
                                                "is_delete",
                                              ]}
                                              hidden
                                            >
                                              <Input type="hidden" />
                                            </Form.Item>
                                            <Form.Item
                                              name={[
                                                packageIndex,
                                                "loose_status",
                                              ]}
                                              hidden
                                            >
                                              <Input type="hidden" />
                                            </Form.Item>
                                          </Col>
                                          <Col span={7}>
                                            <Space>
                                              <Checkbox
                                                checked={
                                                  packageItem?.loose_status ===
                                                  "yes"
                                                }
                                                onChange={() =>
                                                  setLooseStatus("yes")
                                                }
                                              >
                                                Yes
                                              </Checkbox>
                                              <Checkbox
                                                checked={
                                                  packageItem?.loose_status !==
                                                  "yes"
                                                }
                                                onChange={() =>
                                                  setLooseStatus("no")
                                                }
                                              >
                                                No
                                              </Checkbox>
                                            </Space>
                                          </Col>
                                          <Col span={7}>
                                            <Form.Item
                                              name={[
                                                packageIndex,
                                                "package_name",
                                              ]}
                                              style={{ marginBottom: 6 }}
                                              rules={[
                                                {
                                                  required: true,
                                                  message:
                                                    "Roll No is required",
                                                },
                                              ]}
                                            >
                                              <Input
                                                placeholder="Roll No"
                                                disabled={Boolean(
                                                  movementType?.includes?.(
                                                    "issue",
                                                  ),
                                                )}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col span={6}>
                                            <Form.Item
                                              name={[packageIndex, "qty"]}
                                              style={{ marginBottom: 6 }}
                                              rules={[
                                                {
                                                  required: true,
                                                  message: "Qty is required",
                                                },
                                              ]}
                                            >
                                              <InputNumber
                                                min={0}
                                                precision={2}
                                                placeholder="Qty"
                                                style={{ width: "100%" }}
                                              />
                                            </Form.Item>
                                          </Col>
                                          <Col span={2}>
                                            <Space
                                              size={6}
                                              style={{
                                                display: "flex",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <Button
                                                type="primary"
                                                icon={
                                                  <PlusOutlined
                                                    style={{
                                                      color: "#fff",
                                                      display: "inline-flex",
                                                      fontSize: 14,
                                                    }}
                                                  />
                                                }
                                                title="Add row"
                                                style={{
                                                  alignItems: "center",
                                                  display: "inline-flex",
                                                  height: 30,
                                                  justifyContent: "center",
                                                  padding: 0,
                                                  width: 30,
                                                }}
                                                onClick={() =>
                                                  addPackage(
                                                    newPackage(),
                                                    packageIndex + 1,
                                                  )
                                                }
                                              />
                                              <Button
                                                icon={
                                                  <MinusOutlined
                                                    style={{
                                                      color: "#fff",
                                                      display: "inline-flex",
                                                      fontSize: 14,
                                                    }}
                                                  />
                                                }
                                                title="Remove row"
                                                disabled={
                                                  activePackageCount === 1
                                                }
                                                style={{
                                                  alignItems: "center",
                                                  backgroundColor: "#8c8c8c",
                                                  borderColor: "#8c8c8c",
                                                  color: "#fff",
                                                  display: "inline-flex",
                                                  height: 30,
                                                  justifyContent: "center",
                                                  padding: 0,
                                                  width: 30,
                                                }}
                                                onClick={() =>
                                                  removePackage(
                                                    trackIndex,
                                                    packageIndex,
                                                    removePackageField,
                                                  )
                                                }
                                              />
                                            </Space>
                                          </Col>
                                        </Row>
                                      );
                                    },
                                  )}
                                </>
                              )}
                            </Form.List>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() =>
                      add({
                        history_id: 0,
                        track_id: 0,
                        packages: [newPackage()],
                        total_balance: 0,
                        total_shipment: 0,
                        total_quantity: 0,
                        shipment_qty: 1,
                        is_delete: 0,
                      })
                    }
                    style={{
                      borderColor: "#39769d",
                      color: "#39769d",
                      height: 36,
                    }}
                  >
                    Add Warehouse
                  </Button>
                </>
              )
            }
          </Form.List>
        </Form>
      </Spin>
    </Modal>
  );
};

export default FabricStoreEntryEditModal;
